"use client"

import Image from "next/image"
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react"
import {
  MediaAspectSkeleton,
  MediaCoverUnavailable,
} from "@/components/media/media-fallback"
import {
  EVENT_COVER_HERO_IMAGE_CLASS,
  EVENT_COVER_HERO_SIZES,
} from "@/lib/events/event-cover-display"
import { IMAGE_QUALITY_HERO } from "@/lib/media/image-presets"
import {
  appendCacheBustToImageUrl,
  stableImageUrlIdentity,
  stripImageCacheBustParam,
} from "@/lib/media/cloudflare-urls"
import { cn } from "@/lib/utils"

type CoverPhase = "loading" | "ok" | "err"

export type EventCoverHeroStageProps = {
  src: string
  alt: string
  priority?: boolean
  sizes?: string
  /** Overlays inside the fixed-height media box (e.g. upload CTA, archive actions). */
  children?: ReactNode
  /**
   * Optional block absolutely positioned at the bottom of the outer stage
   * (e.g. public event title/metadata stack). Rendered as a sibling after the media box.
   */
  footer?: ReactNode
}

/**
 * Shared event overview hero cover: same fixed heights, object-cover, rounded shell,
 * and bottom-weighted scrim as `/event/[id]`. Keeps archive and live overviews visually aligned.
 * High-fidelity surface — `src` must be a full viewer URL, not a CF thumb variant.
 *
 * Root uses `data-adaptive-luminance="dark"` for any nested `adaptive-text-surface` children.
 * Event detail hero copy overrides with `--cover-hero-text-*` in `event-page-client` because
 * `.type-event-title` / `.type-hero-meta` set theme `foreground` / `text-inverse`, which track
 * the app light/dark chrome—not the photo overlay (see `--text-inverse` in `.dark`).
 */
export function EventCoverHeroStage({
  src,
  alt,
  priority = true,
  sizes = EVENT_COVER_HERO_SIZES,
  children,
  footer,
}: EventCoverHeroStageProps) {
  const [coverPhase, setCoverPhase] = useState<CoverPhase>("loading")
  const [imageSrc, setImageSrc] = useState(src)
  const lastStableIdentityRef = useRef<string | null>(null)
  const loadRetryRef = useRef(0)

  useEffect(() => {
    setImageSrc(src)
    loadRetryRef.current = 0
    const id = stableImageUrlIdentity(src)
    if (id && id === lastStableIdentityRef.current) {
      return
    }
    setCoverPhase("loading")
  }, [src])

  const onCoverLoad = useCallback(() => {
    lastStableIdentityRef.current = stableImageUrlIdentity(imageSrc)
    loadRetryRef.current = 0
    setCoverPhase("ok")
  }, [imageSrc])

  const onCoverError = useCallback(() => {
    if (
      loadRetryRef.current < 2 &&
      imageSrc &&
      !imageSrc.startsWith("blob:") &&
      !imageSrc.startsWith("data:") &&
      !imageSrc.startsWith("/")
    ) {
      loadRetryRef.current += 1
      setImageSrc((prev) =>
        appendCacheBustToImageUrl(stripImageCacheBustParam(prev), Date.now()),
      )
      return
    }
    setCoverPhase("err")
  }, [imageSrc])

  return (
    <div
      className="adaptive-text-surface relative mb-0 max-w-full rounded-2xl sm:rounded-3xl"
      data-adaptive-luminance="dark"
    >
      <div className="relative h-56 max-w-full overflow-hidden rounded-2xl sm:rounded-3xl sm:h-64 lg:h-80">
        {coverPhase === "loading" ? (
          <MediaAspectSkeleton className="z-[1] rounded-[inherit]" shimmer />
        ) : null}
        {coverPhase !== "err" ? (
          <Image
            key={stableImageUrlIdentity(imageSrc)}
            src={imageSrc}
            alt={alt}
            fill
            priority={priority}
            fetchPriority={priority ? "high" : "auto"}
            onLoad={onCoverLoad}
            onError={onCoverError}
            className={cn(
              EVENT_COVER_HERO_IMAGE_CLASS,
              "transition-opacity duration-300",
              coverPhase === "loading" && "opacity-0",
              coverPhase === "ok" && "opacity-100",
            )}
            sizes={sizes}
            quality={IMAGE_QUALITY_HERO}
          />
        ) : null}
        {coverPhase === "err" ? (
          <MediaCoverUnavailable className="z-[2]" />
        ) : null}
        {/* Bottom-zone scrim: fades to transparent above the text area, image shows clean above */}
        <div
          className="overlay-text-scrim z-[2] h-[min(50%,8rem)] sm:h-[min(56%,10.5rem)] lg:h-[min(58%,11.5rem)]"
          aria-hidden
        />
        {children}
      </div>
      {footer}
    </div>
  )
}
